import React, { useState, useEffect, useCallback } from 'react';
import './Gallery.css';

const Gallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  const images = Array.from({ length: 15 }, (_, i) => ({
    id: i + 1,
    src: `/images/gallery/image${i + 1}.jpeg`,
    alt: `Gallery Image ${i + 1}`
  }));

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const handleImageLoad = (imageId) => {
    setLoadedImages(prev => ({ ...prev, [imageId]: true }));
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(images[index].src);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = '';
  }, []);

  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex].src);
  }, [currentIndex, images]);

  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex].src);
  }, [currentIndex, images]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, closeLightbox, nextImage, prevImage]);

  const getVisibleImages = () => {
    if (isMobile) {
      let start = activeIndex - 1;
      if (start < 0) start = 0;
      if (start + 3 > images.length) start = Math.max(0, images.length - 3);
      return images.slice(start, start + 3);
    }
    let start;
    if (activeIndex < 3) start = 0;
    else if (activeIndex > images.length - 4) start = Math.max(0, images.length - 7);
    else start = activeIndex - 1;
    start = Math.max(0, Math.min(start, images.length - 7));
    return images.slice(start, start + 7);
  };

  const visibleImages = getVisibleImages();

  return (
    <section className="gallery-section" id="gallery">
      <div className="gallery-container">
        <div className="gallery-header">
          <h4 className="gallery-subtitle">Our Gallery</h4>
          <h1 className="gallery-title">Visual showcase of our electrical work and installations</h1>
        </div>

        {/* Horizontal Accordion */}
        <div className="gallery-accordion">
          {visibleImages.map((image, idx) => {
            const originalIndex = images.findIndex(img => img.id === image.id);
            const isExpanded = activeIndex === originalIndex;

            return (
              <div
                key={image.id}
                onMouseEnter={() => !isMobile && setActiveIndex(originalIndex)}
                onClick={() => setActiveIndex(originalIndex)}
                className={`gallery-accordion-item ${isExpanded ? 'expanded' : 'collapsed'}`}
              >
                {!loadedImages[image.id] && (
                  <div className="gallery-loader">
                    <div className="loader-spinner"></div>
                  </div>
                )}

                <img
                  src={image.src}
                  alt={image.alt}
                  className={`gallery-accordion-img ${loadedImages[image.id] ? 'loaded' : ''} ${isExpanded ? 'scale-up' : ''}`}
                  onLoad={() => handleImageLoad(image.id)}
                  loading="lazy"
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(originalIndex);
                  }}
                />

                {isExpanded && (
                  <div className="gallery-accordion-overlay">
                    <div className="accordion-content">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openLightbox(originalIndex);
                        }}
                        className="accordion-view-btn"
                      >
                        🔍 View Full Image
                      </button>
                    </div>
                  </div>
                )}

                {!isExpanded && !isMobile && (
                  <div className="gallery-hover-hint">
                    <span>Click to view</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>✕</button>
          <button className="lightbox-nav lightbox-prev" onClick={prevImage}>❮</button>
          <img
            src={selectedImage}
            alt={`Gallery Image ${currentIndex + 1}`}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button className="lightbox-nav lightbox-next" onClick={nextImage}>❯</button>
          <div className="lightbox-counter">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
